<template>
  <div>
    <!-- En-tête : compteur, recherche, filtres et actions. -->
    <div class="px-3 pb-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="mb-0">
          Customers <small class="text-muted">({{ filtered.length }} / {{ customers.length }})</small>
        </h5>
        <b-form-input
          v-model="searchQuery"
          size="sm"
          type="search"
          placeholder="Search name, email, phone, country..."
          style="max-width: 320px;"
        ></b-form-input>
      </div>
      <!-- Filtres : abonnement newsletter et plage de dates de la dernière
           commande. -->
      <div class="d-flex flex-wrap align-items-center">
        <b-form-select v-model="newsletterFilter" :options="newsletterOptions" size="sm" class="w-auto mr-3 mb-2 mb-md-0"></b-form-select>
        <label class="small text-muted mb-0 mr-1">Last order from</label>
        <b-form-input v-model="dateFrom" size="sm" type="date" class="w-auto mr-3 mb-2 mb-md-0"></b-form-input>
        <label class="small text-muted mb-0 mr-1">to</label>
        <b-form-input v-model="dateTo" size="sm" type="date" class="w-auto mr-3 mb-2 mb-md-0"></b-form-input>
        <button
          type="button"
          class="btn btn-light btn-sm mr-2 mb-2 mb-md-0"
          v-if="hasActiveFilters"
          @click="clearFilters"
        ><feather type="x" size="14" class="mr-1"></feather>Clear</button>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm mb-2 mb-md-0 ml-auto"
          :disabled="!filtered.length"
          @click="exportCsv"
        ><feather type="download" size="14" class="mr-1"></feather>Export CSV</button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="text-muted mt-3 mb-0">Loading customers...</p>
    </div>
    <div v-else-if="loadError" class="text-center py-5">
      <p class="text-danger mb-0">{{ loadError }}</p>
    </div>
    <template v-else>
      <div class="table-responsive datatable-vue text-center">
        <b-table
          ref="TableCustomers"
          show-empty
          striped
          hover
          head-variant="light"
          bordered
          stacked="md"
          primary-key="key"
          :items="filtered"
          :fields="tablefields"
          :current-page="currentPage"
          :per-page="perPage"
        >
          <template #empty>
            <span>No customers for this store yet.</span>
          </template>
          <template #cell(name)="field">
            <div style="font-weight:600;">{{ field.item.name || "—" }}</div>
          </template>
          <template #cell(email)="field">
            <a v-if="field.item.email" :href="'mailto:' + field.item.email">{{ field.item.email }}</a>
            <span v-else>—</span>
          </template>
          <template #cell(phone)="field">{{ field.item.phone || "—" }}</template>
          <template #cell(country)="field">{{ field.item.country || "—" }}</template>
          <template #cell(city)="field">{{ field.item.city || "—" }}</template>
          <template #cell(count)="field">{{ field.item.count }}</template>
          <template #cell(spent)="field">{{ formatMoney(field.item.spent, symbol) }}</template>
          <template #cell(newsletter)="field">
            <span v-if="field.item.newsletter" class="badge badge-success">Subscribed</span>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(lastOrder)="field">{{ formatDate(field.item.lastOrder) }}</template>
          <template #cell(actions)="field">
            <button type="button" class="icon-btn" title="View details" @click="field.toggleDetails">
              <feather type="eye" size="16"></feather>
            </button>
          </template>
          <!-- Détail du client : coordonnées + historique de ses commandes. -->
          <template #row-details="row">
            <div class="text-left px-2 py-1">
              <p class="mb-1"><strong>Email:</strong> {{ row.item.email || "—" }} · <strong>Phone:</strong> {{ row.item.phone || "—" }}</p>
              <p class="mb-2"><strong>Location:</strong> {{ row.item.city || "—" }}, {{ row.item.country || "—" }}</p>
              <b-table striped small show-empty :items="row.item.orders" :fields="orderFields">
                <template #empty>
                  <span>No orders.</span>
                </template>
                <template #cell(date)="o">{{ formatDate(o.item.date) }}</template>
                <template #cell(status)="o">{{ o.item.status || "—" }}</template>
                <template #cell(amt)="o">{{ formatMoney(o.item.amt, symbol) }}</template>
              </b-table>
            </div>
          </template>
        </b-table>
      </div>
      <b-col md="12" class="my-1 p-0 pagination-justify" v-if="filtered.length > perPage">
        <b-pagination
          v-model="currentPage"
          :total-rows="filtered.length"
          :per-page="perPage"
          aria-controls="TableCustomers"
          class="mt-4"
        ></b-pagination>
      </b-col>
    </template>
  </div>
</template>

<script>
import firebase from '@/utils/firestore-shim';
import { formatMoney, parseNumber, parseOrderDate, downloadCSV, inDateRange } from "../../../utils/store-stats";

// Nom complet du client depuis le userDetail d'une commande.
function fullName(order) {
  var u = (order && order.userDetail) || {};
  return ((u.firstName || '') + ' ' + (u.lastName || '')).trim();
}

// Date lisible d'une dernière commande.
function formatDate(value) {
  if (!value) return "—";
  try {
    return value.toLocaleDateString();
  } catch (e) {
    return "—";
  }
}

export default {
  name: "table-customers",
  props: ["site"],
  data() {
    return {
      tablefields: [
        { key: "name", label: "Customer", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "phone", label: "Phone" },
        { key: "country", label: "Country", sortable: true },
        { key: "city", label: "City", sortable: true },
        { key: "count", label: "Orders", sortable: true },
        { key: "spent", label: "Total spent", sortable: true },
        { key: "newsletter", label: "Newsletter" },
        { key: "lastOrder", label: "Last order", sortable: true },
        { key: "actions", label: "" }
      ],
      // Colonnes de l'historique affiché dans la vue dépliable d'un client.
      orderFields: [
        { key: "document", label: "Order ID" },
        { key: "date", label: "Date" },
        { key: "status", label: "Status" },
        { key: "amt", label: "Amount" }
      ],
      newsletterOptions: [
        { value: '', text: 'All customers' },
        { value: 'yes', text: 'Newsletter: subscribed' },
        { value: 'no', text: 'Newsletter: not subscribed' }
      ],
      customers: [],
      subscriberEmails: {},
      searchQuery: '',
      newsletterFilter: '',
      dateFrom: '',
      dateTo: '',
      currentPage: 1,
      perPage: 50,
      loading: false,
      loadError: '',
      symbol: ''
    };
  },
  computed: {
    hasActiveFilters() {
      return !!(this.searchQuery.trim() || this.newsletterFilter || this.dateFrom || this.dateTo);
    },
    // Filtrage local (les clients sont déjà tous chargés) : newsletter, plage
    // de dates de la dernière commande puis recherche sur nom, email,
    // téléphone, pays et ville.
    filtered() {
      var q = this.searchQuery.trim().toLowerCase();
      return this.customers.filter((c) => {
        if (this.newsletterFilter === 'yes' && !c.newsletter) return false;
        if (this.newsletterFilter === 'no' && c.newsletter) return false;
        if ((this.dateFrom || this.dateTo) && !inDateRange(c.lastOrder, this.dateFrom, this.dateTo)) return false;
        if (!q) return true;
        var hay = (c.name + ' ' + c.email + ' ' + c.phone + ' ' + c.country + ' ' + c.city).toLowerCase();
        return hay.indexOf(q) !== -1;
      });
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    newsletterFilter() {
      this.currentPage = 1;
    },
    dateFrom() {
      this.currentPage = 1;
    },
    dateTo() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.loadCustomers();
  },
  methods: {
    formatMoney,
    formatDate,
    // Remet tous les filtres à zéro (recherche incluse).
    clearFilters() {
      this.searchQuery = '';
      this.newsletterFilter = '';
      this.dateFrom = '';
      this.dateTo = '';
    },
    // Clients agrégés depuis les commandes réelles de la boutique (même
    // source que le dashboard : collection Commandes filtrée par site,
    // mode_payment non vide = commande payée, pas un panier abandonné).
    async loadCustomers() {
      this.loading = true;
      this.loadError = '';
      try {
        // Emails abonnés à la newsletter : lus en premier pour marquer les
        // clients dans la colonne Newsletter.
        this.subscriberEmails = await this.loadSubscribers();
        var data = [];
        await firebase.firestore().collection('Commandes')
          .where('mode_payment', '!=', '')
          .where('site', 'in', [this.site, 'www.' + this.site])
          .get()
          .then((snapshot) => {
            snapshot.forEach((doc) => {
              var element = doc.data();
              element.document = doc.id;
              data.push(element);
            });
          });
        this.symbol = this.pickSymbol(data);
        this.customers = this.aggregate(data);
      } catch (error) {
        this.loadError = 'Unable to load customers (' + ((error && error.message) || error) + ').';
      } finally {
        this.loading = false;
      }
    },
    // Emails abonnés à la newsletter de la boutique. L'abonnement peut avoir
    // été fait depuis le domaine nu ou le sous-domaine www : on lit les deux
    // chemins et on déduplique par email.
    async loadSubscribers() {
      var emails = {};
      var hosts = [this.site, 'www.' + this.site];
      for (var i = 0; i < hosts.length; i++) {
        try {
          await firebase.firestore().collection('subscribers/' + hosts[i] + '/email')
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                var email = ((doc.data() && doc.data().email) || doc.id || '').trim().toLowerCase();
                if (email) emails[email] = true;
              });
            });
        } catch (e) {
          // Un chemin sans abonné renvoie un snapshot vide ; on ignore les
          // vraies erreurs pour ne pas bloquer l'affichage des clients.
        }
      }
      return emails;
    },
    // Un client = une adresse email (sinon le nom, sinon l'id du document).
    // Nombre de commandes, total dépensé et dernière commande agrégés ; tri
    // par défaut sur le total dépensé (comme le Top customers du dashboard).
    // Chaque commande est conservée pour la vue dépliable du client.
    aggregate(orders) {
      var map = {};
      orders.forEach((o) => {
        var u = o.userDetail || {};
        var key = (u.email || '').trim().toLowerCase() || fullName(o) || o.document;
        if (!map[key]) {
          map[key] = {
            key: key,
            name: fullName(o),
            email: u.email || '',
            phone: u.phone || '',
            country: u.country || '',
            city: u.city || '',
            count: 0,
            spent: 0,
            newsletter: !!this.subscriberEmails[key],
            lastOrder: null,
            orders: []
          };
        }
        var c = map[key];
        c.count += 1;
        c.spent += parseNumber(o.amt);
        if (!c.phone && u.phone) c.phone = u.phone;
        if (!c.country && u.country) c.country = u.country;
        if (!c.city && u.city) c.city = u.city;
        if (!c.name) c.name = fullName(o);
        var d = parseOrderDate(o.orderDate);
        if (d && (!c.lastOrder || d.getTime() > c.lastOrder.getTime())) c.lastOrder = d;
        c.orders.push({
          document: o.document,
          date: d,
          status: o.status || '',
          amt: parseNumber(o.amt)
        });
      });
      // Historique de chaque client : commandes les plus récentes en premier.
      Object.keys(map).forEach((k) => {
        map[k].orders.sort((a, b) => {
          var ta = a.date ? a.date.getTime() : 0;
          var tb = b.date ? b.date.getTime() : 0;
          return tb - ta;
        });
      });
      return Object.values(map).sort((a, b) => b.spent - a.spent);
    },
    // Symbole monétaire : repris de la première commande qui en porte un.
    pickSymbol(orders) {
      for (var i = 0; i < orders.length; i++) {
        if (orders[i].symbol) return orders[i].symbol;
      }
      return '';
    },
    // Export CSV des clients filtrés.
    exportCsv() {
      var rows = this.filtered.map((c) => ({
        Customer: c.name,
        Email: c.email,
        Phone: c.phone,
        Country: c.country,
        City: c.city,
        Orders: c.count,
        'Total spent': c.spent,
        Newsletter: c.newsletter ? 'Yes' : 'No',
        'Last order': c.lastOrder ? c.lastOrder.toLocaleDateString() : ''
      }));
      downloadCSV(rows, 'customers-' + this.site + '-' + new Date().toISOString().slice(0, 10) + '.csv');
    }
  }
};
</script>

<style></style>
